import { useState, useEffect, useRef } from "react";
import angle_down from "../assets/icons/angle-down.svg";
import { cn } from "../lib/utils";

function useOutsideClick(ref, callback) {
  useEffect(() => {
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        callback();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref, callback]);
}

export default function PhoneInput({
  countryOptions,
  value,
  onChange,
  state = "default",
  helperText = false,
  additionalInfo = false,
  country,
  isFirstCountrySelection,
  setIsFirstCountrySelection,
  countryCode,
}) {
  const getInitialCountry = () => {
    if (countryCode) {
      const foundByCode = countryOptions.find(
        (opt) => opt.value === countryCode || opt.value.includes(countryCode)
      );
      if (foundByCode) return foundByCode;
    }
    if (country) {
      const foundByName = countryOptions.find(
        (opt) => opt.name?.toLowerCase() === country?.toLowerCase()
      );
      if (foundByName) return foundByName;
    }
    return countryOptions[0];
  };

  const [selected, setSelected] = useState(getInitialCountry());
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  useOutsideClick(dropdownRef, () => {
    if (open) setOpen(false);
  });

  useEffect(() => {
    if (country && isFirstCountrySelection) {
      const found = countryOptions.find((opt) => opt.name === country);
      if (found) setSelected(found);
      setIsFirstCountrySelection(false);
    }
  }, [country, countryOptions]);

  const handleInputChange = (e) => {
    const phoneOnly = e.target.value?.replace(selected.value, "").trim() || "";
    onChange?.(phoneOnly, selected.value);
  };

  const handleCountrySelect = (opt) => {
    setSelected(opt);
    setOpen(false);
    setSearch("");
    const phoneOnly = value?.replace(selected.value, "").trim() || "";
    onChange?.(phoneOnly, opt.value);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const stateStyles = {
    default: "border-[#CECBD7]",
    error: "border-red-500",
    success: "border-green-500",
    disabled: "bg-muted text-muted-foreground cursor-not-allowed opacity-70",
  };

  const filteredOptions = countryOptions.filter((opt) => {
    const name = opt.name?.toLowerCase() || "";
    const valueCode = opt.value?.toLowerCase() || "";
    return (
      name.includes(search.toLowerCase()) ||
      valueCode.includes(search.toLowerCase())
    );
  });

  return (
    <div className="w-full space-y-1">
      <p className="block text-[14px] text-[#6D6584] mb-1">Cellphone number</p>

      <div className="flex w-full">
        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            disabled={state === "disabled"}
            onClick={() => setOpen(!open)}
            className={cn(
              "flex items-center justify-center px-3 pt-[15px] pb-[15px] bg-white border border-r-0 rounded-l-md",
              stateStyles[state]
            )}
          >
            <img
              src={selected.flag}
              alt={selected.name}
              className="w-6 h-6 rounded-sm"
            />
            <img src={angle_down} alt="dropdown" className="w-4 h-4 ml-2" />
          </button>

          {open && (
            <div className="absolute top-full left-0 mt-1 bg-white rounded-md shadow-lg z-20 max-h-60 overflow-hidden min-w-[150px]">
              <div className="p-2 border-b">
                <div className="relative">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 1110.5 3a7.5 7.5 0 016.15 13.65z"
                    />
                  </svg>
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search"
                    className="w-full pl-9 pr-3 py-2 text-sm rounded-full border border-gray-300 outline-none "
                    autoFocus
                  />
                </div>
              </div>
              <ul className="max-h-48 overflow-y-auto">
                {filteredOptions.length > 0 ? (
                  filteredOptions.map((opt, idx) => (
                    <li
                      key={idx}
                      onClick={() => handleCountrySelect(opt)}
                      className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-100"
                    >
                      <img
                        src={opt.flag}
                        alt={opt.name}
                        className="w-6 h-6 rounded-sm"
                      />
                      <span>{opt.value}</span>
                    </li>
                  ))
                ) : (
                  <li className="px-3 py-2 text-sm text-gray-500">
                    No results
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>

        <input
          ref={inputRef}
          type="tel"
          disabled={state === "disabled"}
          value={`${selected.value} ${value}`}
          onChange={handleInputChange}
          className={cn(
            "flex-1 px-3 py-3 border border-l-0 rounded-r-md outline-none",
            stateStyles[state]
          )}
        />
      </div>

      {helperText && (
        <p
          className={cn(
            "text-xs mt-1",
            state === "error" ? "text-red-500" : "text-muted-foreground"
          )}
        >
          {helperText}
        </p>
      )}
      {additionalInfo && (
        <p className="text-xs text-muted-foreground">{additionalInfo}</p>
      )}
    </div>
  );
}
